import React, { useState, useEffect, useCallback, ChangeEvent } from 'react'; 
import { useParams, useNavigate } from 'react-router-dom';
import { pacientesService as pacienteService, catalogoService, type CriarPacienteInput } from '../services/api';
import FiltroPsicologo from '../components/FiltroPsicologo';

export default function FormPaciente() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  
  // Estados para controle de passos do formulário
  const [passoAtual, setPassoAtual] = useState(1);
  const totalPassos = 5;
  
  // modoVisualizacao não é mais necessário (controle simplificado)
  // (removido) const modoVisualizacao = false; // não utilizado
  
  // MELHORIA: Função para verificar se está em modo de edição
  const modoEdicao = !!id;

  // Estados para validação
  const [erros, setErros] = useState<Record<string, string>>({});
  const [sucessoMensagem, setSucessoMensagem] = useState('');
  
  // Estados tipados corretamente
  const [nome, setNome] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [servico, setServico] = useState<'psicoterapia' | 'avaliacao' | 'orientacao' | 'terapia_casal' | 'triagem'>('psicoterapia');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [estado_civil, setEstadoCivil] = useState<string>('');
  const [profissao, setProfissao] = useState('');
  const [escolaridade, setEscolaridade] = useState<string>(''); 
  const [genero, setGenero] = useState<string>(''); 
  
  // Separate address fields
  const [endereco, setEndereco] = useState('');
  const [cep, setCep] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  
  // Emergency contact fields
  const [nome_emergencia, setNomeEmergencia] = useState('');
  const [telefone_emergencia, setTelefoneEmergencia] = useState('');
  const [parentesco_emergencia, setParentescoEmergencia] = useState('');
  
  const [observacoes, setObservacoes] = useState('');
  const [linkConsultaOnline, setLinkConsultaOnline] = useState('');
  const [dataPrimeiraConsulta, setDataPrimeiraConsulta] = useState('');

  // ✅ MULTI-PSICÓLOGOS: Psicólogo responsável
  const [usuarioResponsavel, setUsuarioResponsavel] = useState<number | null>(null);

  // Histórico médico e problemas clínicos
  const [historicoMedico, setHistoricoMedico] = useState('');
  const [problemasClinicos, setProblemasClinicos] = useState<Record<string, boolean>>({});
  
  // Estado para mensagem de erro
  const [erro, setErro] = useState('');
  
  // CORREÇÃO: Estados simplificados sem dependências circulares
  const [problemasCatalogoLista, setProblemasCatalogoLista] = useState<Array<{id: string, label: string}>>([]);
  const [catalogosCarregados, setCatalogosCarregados] = useState(false);
  
  // Detectar cargo do usuário para regras de exibição
  const cargoUsuario = (() => {
    try {
      const rawUser = sessionStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        return parsed?.cargo || '';
      }
    } catch (e) {
      console.warn('[FormPaciente] Falha ao determinar cargo do usuário', e);
    }
    return '';
  })();
  const isAtendente = cargoUsuario === 'atendente';

  // LOG: Adicionar logs para diagnosticar re-renders
  console.log('[FormPaciente] Render iniciado:', {
    passoAtual,
    carregando,
    salvando,
    catalogosCarregados,
    problemasQtd: Object.keys(problemasClinicos).length,
    catalogoQtd: problemasCatalogoLista.length,
    cargoUsuario,
    isAtendente,
    timestamp: Date.now()
  });
  
  // CORREÇÃO: Funções de formatação estáveis com useMemo
  const formatarCPF = useCallback((valor: string): string => {
    console.log('[formatarCPF] Chamado com:', valor);
    const somenteNumeros = valor.replace(/\D/g, '');
    const limitado = somenteNumeros.slice(0, 11);
    
    if (limitado.length >= 4) {
      return limitado
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2');
    }
    
    return limitado;
  }, []);

  const formatarRG = useCallback((valor: string): string => {
    console.log('[formatarRG] Chamado com:', valor);
    const valorLimpo = valor.replace(/[^0-9X]/gi, '').toUpperCase();
    const limitado = valorLimpo.slice(0, 9);
    
    if (limitado.length >= 2) {
      return limitado
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1})/, '$1-$2');
    }
    
    return limitado;
  }, []);

  // CORREÇÃO: Handlers completamente estáveis - SEM preventDefault que causa problemas
  const handleCPFChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    console.log('[handleCPFChange] Executado com valor:', e.target.value);
    const valorFormatado = formatarCPF(e.target.value);
    setCpf(valorFormatado);
    
    // Remover erros relacionados ao CPF
    setErros(prev => {
      if (prev.cpf) {
        const { cpf: _, ...novosErros } = prev;
        return novosErros;
      }
      return prev;
    });
  }, [formatarCPF]);

  const handleRGChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    console.log('[handleRGChange] Executado com valor:', e.target.value);
    const valorFormatado = formatarRG(e.target.value);
    setRg(valorFormatado);
  }, [formatarRG]);

  // CORREÇÃO: Handlers simples para campos de texto
  const handleNomeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    console.log('[handleNomeChange] Executado com valor:', e.target.value);
    setNome(e.target.value);
    if (erros.nome) {
      setErros(prev => {
        const { nome: _, ...novosErros } = prev;
        return novosErros;
      });
    }
  }, [erros.nome]);

  const handleTelefoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    console.log('[handleTelefoneChange] Executado com valor:', e.target.value);
    setTelefone(e.target.value);
    if (erros.telefone) {
      setErros(prev => {
        const { telefone: _, ...novosErros } = prev;
        return novosErros;
      });
    }
  }, [erros.telefone]);

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    console.log('[handleEmailChange] Executado com valor:', e.target.value);
    setEmail(e.target.value);
    if (erros.email) {
      setErros(prev => {
        const { email: _, ...novosErros } = prev;
        return novosErros;
      });
    }
  }, [erros.email]);

  // CORREÇÃO: Handlers para selects - estáveis
  const handleEscolaridadeChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    console.log('[handleEscolaridadeChange] Executado com valor:', e.target.value);
    setEscolaridade(e.target.value);
  }, []);

  const handleEstadoCivilChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    console.log('[handleEstadoCivilChange] Executado com valor:', e.target.value);
    setEstadoCivil(e.target.value);
  }, []);

  const handleGeneroChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    console.log('[handleGeneroChange] Executado com valor:', e.target.value);
    setGenero(e.target.value);
  }, []);

  // CORREÇÃO: Carregar catálogos uma única vez - sem dependências problemáticas
  useEffect(() => {
    let mounted = true;
    
    const carregarCatalogos = async () => {
      if (catalogosCarregados) {
        console.log('[carregarCatalogos] Já carregados, pulando...');
        return;
      }
      
      try {
        console.log('[carregarCatalogos] Iniciando carregamento...');
        
        const catalogos = await catalogoService.obterTodos();
        
        if (mounted && catalogos && Array.isArray(catalogos.problemasClinicos)) {
          console.log('[carregarCatalogos] Carregados com sucesso:', catalogos.problemasClinicos.length, 'problemas');
          setProblemasCatalogoLista(catalogos.problemasClinicos);
          
          // Inicializar problemas apenas se necessário
          if (Object.keys(problemasClinicos).length === 0) {
            console.log('[carregarCatalogos] Inicializando problemas clínicos...');
            const problemasIniciais: Record<string, boolean> = {};
            catalogos.problemasClinicos.forEach(problema => {
              problemasIniciais[problema.id] = false;
            });
            setProblemasClinicos(problemasIniciais);
          }
          
          setCatalogosCarregados(true);
        }
      } catch (error) {
        console.error('[carregarCatalogos] Erro:', error);
        if (mounted) {
          setErro('Não foi possível carregar os catálogos.');
        }
      }
    };
    
    carregarCatalogos();
    
    return () => {
      mounted = false;
    };
  }, []); // CRÍTICO: Array vazio para executar apenas uma vez
  
  // CORREÇÃO: Carregar dados do paciente - simplificado
  useEffect(() => {
    let mounted = true;
    
    const carregarPaciente = async () => {
      if (!id) return;
      
      try {
        setCarregando(true);
        console.log('[carregarPaciente] Carregando paciente ID:', id);
        
        const paciente = await pacienteService.obterPorId(id);
        
        if (mounted && paciente) {
          console.log('[carregarPaciente] Dados recebidos:', {
            nome: paciente.nome,
            telefone: paciente.telefone,
            email: paciente.email,
            linkConsultaOnline: paciente.linkConsultaOnline,
            problemasClinicos: !!paciente.problemasClinicos,
            todasAsChaves: Object.keys(paciente)
          });
          
          // Preencher campos básicos
          setNome(paciente.nome || '');
          setDataNascimento(paciente.dataNascimento || '');
          setServico(paciente.servico || 'psicoterapia');
          setTelefone(paciente.telefone || '');
          setEmail(paciente.email || '');
          setCpf(paciente.cpf ? formatarCPF(paciente.cpf) : '');
          setRg(paciente.rg ? formatarRG(paciente.rg) : '');
          setEstadoCivil(paciente.estado_civil || '');
          setEscolaridade(paciente.escolaridade || '');
          setEndereco(paciente.endereco || '');
          setCep(paciente.cep || '');
          setCidade(paciente.cidade || '');
          setEstado(paciente.estado || '');
          setProfissao(paciente.profissao || '');
          setGenero(paciente.genero || '');
          setObservacoes(paciente.observacoes || '');
          setLinkConsultaOnline(paciente.linkConsultaOnline || '');
          console.log('[carregarPaciente] Link consulta online carregado:', paciente.linkConsultaOnline);
          setDataPrimeiraConsulta(paciente.dataPrimeiraConsulta || '');
          setNomeEmergencia(paciente.nome_emergencia || '');
          setTelefoneEmergencia(paciente.telefone_emergencia || '');
          setParentescoEmergencia(paciente.parentesco_emergencia || '');
          setHistoricoMedico(paciente.historicoMedico || '');
          
          // ✅ MULTI-PSICÓLOGOS: Carregar psicólogo responsável
          setUsuarioResponsavel((paciente as any).usuario_responsavel || null);
          
          // Carregar problemas clínicos se existirem
          if (paciente.problemasClinicos && typeof paciente.problemasClinicos === 'object') {
            console.log('[carregarPaciente] Carregando problemas clínicos:', Object.keys(paciente.problemasClinicos).length);
            setProblemasClinicos(paciente.problemasClinicos);
          }
          
          // NOVA LINHA: Ativar modo visualização para edição de paciente
          // modoVisualizacao descontinuado
        }
      } catch (error) {
        console.error('[carregarPaciente] Erro:', error);
        if (mounted) {
          setErro('Não foi possível carregar os dados do paciente.');
        }
      } finally {
        if (mounted) {
          setCarregando(false);
        }
      }
    };
    
    carregarPaciente();
    
    return () => {
      mounted = false;
    };
  }, [id]); // CORREÇÃO: Apenas dependência do ID

  // CORREÇÃO: Funções de validação e navegação estáveis
  // Remover função não utilizada
  // const isValidEmail = useCallback((email: string): boolean => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   return emailRegex.test(email);
  // }, []);

  // CORREÇÃO RADICAL: Remover completamente a função validarPasso
  // const validarPasso = useCallback((passo: number): boolean => { ... }, []);

  // CORREÇÃO: Simplificar navegação sem validações
  const irParaPasso = useCallback((passo: number) => {
    setPassoAtual(passo);
  }, []);

  const proximoPasso = useCallback((e: React.MouseEvent) => {
    // Prevenção explícita da propagação do evento que pode causar o submit prematuro
    e.preventDefault();
    e.stopPropagation();
    
    if (passoAtual < totalPassos) {
      setPassoAtual(passoAtual + 1);
    }
  }, [passoAtual, totalPassos]);

  const passoAnterior = useCallback(() => {
    if (passoAtual > 1) {
      setPassoAtual(passoAtual - 1);
    }
  }, [passoAtual]);

  // CORREÇÃO: Manter apenas UMA declaração do componente ProblemaClinicoCheckbox
  const ProblemaClinicoCheckbox = ({ 
    problemaId,
    label,
    checked,
    onChange
  }: {
    problemaId: string;
    label: string;
    checked: boolean;
    onChange: (id: string, checked: boolean) => void;
  }) => {
    return (
      <div className="custom-control custom-checkbox">
        <input
          type="checkbox"
          className="custom-control-input"
          id={`problema-${problemaId}`}
          checked={checked}
          onChange={(e) => onChange(problemaId, e.target.checked)}
        />
        <label className="custom-control-label" htmlFor={`problema-${problemaId}`}>
          {label}
        </label>
      </div>
    );
  };

  // CORREÇÃO: Manter apenas UMA declaração do handler
  const handleProblemaClinicoChange = useCallback((problemaId: string, isChecked: boolean) => {
    setProblemasClinicos(prev => ({
      ...prev,
      [problemaId]: isChecked
    }));
  }, []);

  // CORREÇÃO: handleSubmit simplificado
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[handleSubmit] Iniciando submissão...');
    console.log('[handleSubmit] Valor do linkConsultaOnline:', linkConsultaOnline, 'tipo:', typeof linkConsultaOnline);
    console.log('[handleSubmit] Todos os valores de estado:', {
      nome,
      linkConsultaOnline,
      telefone,
      email,
      observacoes,
      historicoMedico
    });
    
    if (salvando) {
      console.log('[handleSubmit] Já salvando, ignorando...');
      return;
    }
    
    try {
      setSalvando(true);
      
      const cpfLimpo = cpf.replace(/\D/g, '');
      const rgLimpo = rg.replace(/[^0-9X]/gi, '');
      
      const pacienteData: CriarPacienteInput = {
        nome,
        dataNascimento: dataNascimento || undefined,
        servico,
        telefone,
        email: email || undefined,
        cpf: cpfLimpo || undefined,
        rg: rgLimpo || undefined,
        estado_civil: estado_civil as any || undefined,
        escolaridade: escolaridade as any || undefined,
        genero: genero as any || undefined,
        endereco: endereco || undefined,
        cep: cep || undefined,
        cidade: cidade || undefined,
        estado: estado || undefined,
        nome_emergencia: nome_emergencia || undefined,
        telefone_emergencia: telefone_emergencia || undefined,
        parentesco_emergencia: parentesco_emergencia || undefined,
        // Campos sensíveis ocultos para ATENDENTE (não enviar)
        observacoes: isAtendente ? undefined : (observacoes || undefined),
        linkConsultaOnline: linkConsultaOnline || '',
        historicoMedico: isAtendente ? undefined : (historicoMedico || undefined),
        problemasClinicos: isAtendente ? undefined : (Object.keys(problemasClinicos).length > 0 ? problemasClinicos : undefined),
        dataPrimeiraConsulta: dataPrimeiraConsulta || undefined,
        profissao: profissao || undefined,
        usuario_responsavel: usuarioResponsavel || undefined  // ✅ MULTI-PSICÓLOGOS
      };
      
      console.log('[FormPaciente-handleSubmit] Dados a serem enviados:', { ...pacienteData, problemasClinicos: 'object' });
      console.log('[FormPaciente-handleSubmit] ✅ usuario_responsavel:', usuarioResponsavel);
      
      if (id) {
        await pacienteService.atualizar(id, pacienteData);
        console.log('[handleSubmit] Paciente atualizado com sucesso');
        alert('Paciente atualizado com sucesso!');
      } else {
        try {
          await pacienteService.criar(pacienteData);
          console.log('[handleSubmit] Paciente criado com sucesso');
          alert('Paciente cadastrado com sucesso!');
        } catch (err: any) {
          // Interceptar limite estudante 429
            if (err?.status === 429 || err?.response?.status === 429) {
              alert('Limite de 5 pacientes da conta estudante atingido. Para cadastrar mais pacientes, atualize seu plano.');
              return; // não navegar
            }
            throw err; // outros erros seguem fluxo normal
        }
      }
      
      navigate('/pacientes');
    } catch (error) {
      console.error('[handleSubmit] Erro:', error);
      setErro('Ocorreu um erro ao salvar os dados. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }, [salvando, cpf, rg, nome, dataNascimento, servico, telefone, email, estado_civil, escolaridade, genero, endereco, cep, cidade, estado, nome_emergencia, telefone_emergencia, parentesco_emergencia, observacoes, linkConsultaOnline, historicoMedico, problemasClinicos, dataPrimeiraConsulta, profissao, usuarioResponsavel, id, navigate]);

  // CORREÇÃO: Calcular progresso de forma estável
  const progresso = useCallback((): number => {
    const camposObrigatorios = [
      { campo: nome.trim(), peso: 25 },
      { campo: telefone.trim(), peso: 25 }
    ];
    
    const camposOpcionais = [
      { campo: email.trim(), peso: 10 },
      { campo: cpf.trim(), peso: 10 },
      { campo: endereco.trim(), peso: 10 },
      { campo: profissao.trim(), peso: 10 },
  { campo: isAtendente ? '' : observacoes.trim(), peso: 5 },
  { campo: isAtendente ? '' : historicoMedico.trim(), peso: 5 }
    ];
    
    const progressoObrigatorio = camposObrigatorios
      .reduce((total, { campo, peso }) => total + (campo ? peso : 0), 0);
    
    const progressoOpcional = camposOpcionais
      .reduce((total, { campo, peso }) => total + (campo ? peso : 0), 0);
    
    return Math.min(progressoObrigatorio + progressoOpcional, 100);
  }, [nome, telefone, email, cpf, endereco, profissao, observacoes, historicoMedico]);

  // CORREÇÃO: useEffect para garantir o modo visualizacao ativado na edição
  useEffect(() => {
    // Se tiver ID (editando paciente), sempre ativar o modo visualizacao
    if (id) {
      console.log('[useEffect] Ativando modo visualização para paciente existente');
  // modoVisualizacao descontinuado
    }
  }, [id]);

  // Função para renderizar o conteúdo baseado no passo atual
  const renderizarConteudoPasso = (passo: number) => {
    switch (passo) {
      case 1:
        return (
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title">
                <i className="fas fa-user mr-2"></i>
                Dados Básicos
              </h3>
            </div>
            <div className="card-body">
              {/* Conteúdo do passo 1 existente */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="nome" className="form-label">
                      <i className="fas fa-user mr-1"></i>
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${erros.nome ? 'is-invalid' : nome.trim() ? 'is-valid' : ''}`}
                      id="nome"
                      value={nome}
                      onChange={handleNomeChange}
                      placeholder="Digite o nome completo"
                      required
                    />
                    {erros.nome && <div className="invalid-feedback">{erros.nome}</div>}
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="telefone" className="form-label">
                      <i className="fas fa-phone mr-1"></i>
                      Telefone *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${erros.telefone ? 'is-invalid' : telefone.trim() ? 'is-valid' : ''}`}
                      id="telefone"
                      value={telefone}
                      onChange={handleTelefoneChange}
                      placeholder="(11) 99999-9999"
                      required
                    />
                    {erros.telefone && <div className="invalid-feedback">{erros.telefone}</div>}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="dataNascimento" className="form-label">
                      <i className="fas fa-calendar mr-1"></i>
                      Data de Nascimento
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="dataNascimento"
                      value={dataNascimento}
                      onChange={(e) => setDataNascimento(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="genero" className="form-label">
                      <i className="fas fa-venus-mars mr-1"></i>
                      Gênero
                    </label>
                    <select
                      className={`form-control ${erros.genero ? 'is-invalid' : ''}`}
                      id="genero"
                      value={genero}
                      onChange={handleGeneroChange}
                    >
                      <option value="">Selecione...</option>
                      <option value="masculino">Masculino</option>
                      <option value="feminino">Feminino</option>
                      <option value="outro">Outro</option>
                      <option value="nao_informar">Prefiro não informar</option>
                    </select>
                    {erros.genero && <div className="invalid-feedback">{erros.genero}</div>}
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="servico" className="form-label">
                      <i className="fas fa-stethoscope mr-1"></i>
                      Tipo de Serviço *
                    </label>
                    <select
                      className="form-control"
                      id="servico"
                      value={servico}
                      onChange={(e) => setServico(e.target.value as any)}
                      required
                    >
                      <option value="psicoterapia">Psicoterapia</option>
                      <option value="avaliacao">Avaliação Psicológica</option>
                      <option value="orientacao">Orientação</option>
                      <option value="terapia_casal">Terapia de Casal</option>
                      <option value="triagem">Triagem</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="cpf" className="form-label">
                      <i className="fas fa-id-card mr-1"></i>
                      CPF
                    </label>
                    <input
                      type="text"
                      className={`form-control ${erros.cpf ? 'is-invalid' : ''}`}
                      id="cpf"
                      value={cpf}
                      onChange={handleCPFChange}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    {erros.cpf && <div className="invalid-feedback">{erros.cpf}</div>}
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      <i className="fas fa-envelope mr-1"></i>
                      E-mail
                    </label>
                    <input
                      type="email"
                      className={`form-control ${erros.email ? 'is-invalid' : email.includes('@') && email.trim() ? 'is-valid' : ''}`}
                      id="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="nome@exemplo.com"
                    />
                    {erros.email && <div className="invalid-feedback">{erros.email}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="card">
            <div className="card-header bg-info text-white">
              <h3 className="card-title">
                <i className="fas fa-address-card mr-2"></i>
                Dados Complementares
              </h3>
            </div>
            <div className="card-body">
              {/* Conteúdo do passo 2 existente */}
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="rg" className="form-label">
                      <i className="fas fa-id-badge mr-1"></i>
                      RG
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="rg"
                      value={rg}
                      onChange={handleRGChange}
                      placeholder="00.000.000-0"
                      maxLength={12}
                    />
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="estado_civil" className="form-label">
                      <i className="fas fa-heart mr-1"></i>
                      Estado Civil
                    </label>
                    <select
                      className="form-control"
                      id="estado_civil"
                      value={estado_civil}
                      onChange={handleEstadoCivilChange}
                    >
                      <option value="">Selecione...</option>
                      <option value="solteiro">Solteiro(a)</option>
                      <option value="casado">Casado(a)</option>
                      <option value="divorciado">Divorciado(a)</option>
                      <option value="viuvo">Viúvo(a)</option>
                      <option value="uniao_estavel">União Estável</option>
                    </select>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="profissao" className="form-label">
                      <i className="fas fa-briefcase mr-1"></i>
                      Profissão
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="profissao"
                      value={profissao}
                      onChange={(e) => setProfissao(e.target.value)}
                      placeholder="Ex: Engenheiro, Professor"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="escolaridade" className="form-label">
                      <i className="fas fa-graduation-cap mr-1"></i>
                      Escolaridade
                    </label>
                    <select
                      className="form-control"
                      id="escolaridade"
                      value={escolaridade}
                      onChange={handleEscolaridadeChange}
                    >
                      <option value="">Selecione...</option>
                      <option value="fundamental_incompleto">Fundamental Incompleto</option>
                      <option value="fundamental_completo">Fundamental Completo</option>
                      <option value="medio_incompleto">Médio Incompleto</option>
                      <option value="medio_completo">Médio Completo</option>
                      <option value="superior_incompleto">Superior Incompleto</option>
                      <option value="superior_completo">Superior Completo</option>
                      <option value="pos_graduacao">Pós-graduação</option>
                      <option value="mestrado">Mestrado</option>
                      <option value="doutorado">Doutorado</option>
                    </select>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="dataPrimeiraConsulta" className="form-label">
                      <i className="fas fa-calendar-check mr-1"></i>
                      Data da Primeira Consulta
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="dataPrimeiraConsulta"
                      value={dataPrimeiraConsulta}
                      onChange={(e) => setDataPrimeiraConsulta(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="endereco" className="form-label">
                      <i className="fas fa-map-marker-alt mr-1"></i>
                      Endereço
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="endereco"
                      value={endereco}
                      onChange={(e) => setEndereco(e.target.value)}
                      placeholder="Rua, número, bairro"
                    />
                  </div>
                </div>
                
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="cep" className="form-label">
                      <i className="fas fa-mail-bulk mr-1"></i>
                      CEP
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="cep"
                      value={cep}
                      onChange={(e) => setCep(e.target.value)}
                      placeholder="00000-000"
                    />
                  </div>
                </div>
                
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="cidade" className="form-label">
                      <i className="fas fa-city mr-1"></i>
                      Cidade
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="cidade"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      placeholder="Nome da cidade"
                    />
                  </div>
                </div>
                
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="estado" className="form-label">
                      <i className="fas fa-map mr-1"></i>
                      Estado
                    </label>
                    <select
                      className="form-control"
                      id="estado"
                      value={estado}
                      onChange={(e) => setEstado(e.target.value)}
                    >
                      <option value="">UF</option>
                      <option value="SP">SP</option>
                      <option value="AC">AC</option>
                      <option value="AL">AL</option>
                      <option value="AP">AP</option>
                      <option value="AM">AM</option>
                      <option value="BA">BA</option>
                      <option value="CE">CE</option>
                      <option value="DF">DF</option>
                      <option value="ES">ES</option>
                      <option value="GO">GO</option>
                      <option value="MA">MA</option>
                      <option value="MT">MT</option>
                      <option value="MS">MS</option>
                      <option value="MG">MG</option>
                      <option value="PA">PA</option>
                      <option value="PB">PB</option>
                      <option value="PR">PR</option>
                      <option value="PE">PE</option>
                      <option value="PI">PI</option>
                      <option value="RJ">RJ</option>
                      <option value="RN">RN</option>
                      <option value="RS">RS</option>
                      <option value="RO">RO</option>
                      <option value="RR">RR</option>
                      <option value="SC">SC</option>
                      <option value="SP">SP</option>
                      <option value="SE">SE</option>
                      <option value="TO">TO</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="card">
            <div className="card-header bg-warning">
              <h3 className="card-title">
                <i className="fas fa-phone-alt mr-2"></i>
                Contato de Emergência
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="nome_emergencia" className="form-label">
                      <i className="fas fa-user-friends mr-1"></i>
                      Nome do Contato
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="nome_emergencia"
                      value={nome_emergencia}
                      onChange={(e) => setNomeEmergencia(e.target.value)}
                      placeholder="Nome completo do contato"
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="telefone_emergencia" className="form-label">
                      <i className="fas fa-phone mr-1"></i>
                      Telefone do Contato
                    </label>
                    <input
                      type="text"
                      className={`form-control ${erros.telefone_emergencia ? 'is-invalid' : ''}`}
                      id="telefone_emergencia"
                      value={telefone_emergencia}
                      onChange={(e) => setTelefoneEmergencia(e.target.value)}
                      placeholder="(11) 99999-9999"
                    />
                    {erros.telefone_emergencia && (
                      <div className="invalid-feedback">{erros.telefone_emergencia}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* NOVO: Campo de parentesco do contato de emergência */}
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="parentesco_emergencia" className="form-label">
                      <i className="fas fa-users mr-1"></i>
                      Parentesco/Relação
                    </label>
                    <select
                      className="form-control"
                      id="parentesco_emergencia"
                      value={parentesco_emergencia}
                      onChange={(e) => setParentescoEmergencia(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      <option value="conjuge">Cônjuge</option>
                      <option value="pai">Pai</option>
                      <option value="mae">Mãe</option>
                      <option value="filho">Filho(a)</option>
                      <option value="irmao">Irmão/Irmã</option>
                      <option value="amigo">Amigo(a)</option>
                      <option value="outro">Outro</option>
                    </select>
                    <small className="form-text text-muted">
                      Indique o relacionamento desta pessoa com o paciente
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="card">
            <div className="card-header bg-success text-white">
              <h3 className="card-title">
                <i className="fas fa-file-medical mr-2"></i>
                {isAtendente ? 'Responsável e Link Online' : 'Histórico Médico e Observações'}
              </h3>
            </div>
            <div className="card-body">
              {/* ✅ MULTI-PSICÓLOGOS: Campo de Psicólogo Responsável */}
              <div className="row">
                <div className="col-md-6">
                  <FiltroPsicologo
                    value={usuarioResponsavel}
                    onChange={setUsuarioResponsavel}
                    incluirTodos={false}
                    label="Psicólogo Responsável *"
                  />
                  <small className="form-text text-muted">
                    <i className="fas fa-info-circle mr-1"></i>
                    Selecione o psicólogo que será responsável pelo atendimento deste paciente
                  </small>
                </div>
              </div>
              {!isAtendente && (
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor="historicoMedico" className="form-label">
                        <i className="fas fa-notes-medical mr-1"></i>
                        Histórico Médico
                      </label>
                      <textarea
                        className="form-control"
                        id="historicoMedico"
                        rows={4}
                        value={historicoMedico}
                        onChange={(e) => setHistoricoMedico(e.target.value)}
                        placeholder="Descreva o histórico médico relevante do paciente..."
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label htmlFor="linkConsultaOnline" className="form-label">
                      <i className="fas fa-video mr-1"></i>
                      Link da Consulta Online
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="linkConsultaOnline"
                      value={linkConsultaOnline}
                      onChange={(e) => {
                        console.log('[linkConsultaOnline] Valor alterado:', e.target.value);
                        setLinkConsultaOnline(e.target.value);
                      }}
                      placeholder="https://meet.google.com/abc-defg-hij ou https://zoom.us/j/123456789"
                    />
                    <small className="form-text text-muted">
                      Link para sala de consulta online (Google Meet, Zoom, Teams, etc.)
                    </small>
                  </div>
                </div>
              </div>

              {!isAtendente && (
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <div className="form-label d-flex justify-content-between align-items-center">
                        <span>
                          <i className="fas fa-clipboard-list mr-1"></i>
                          Problemas Clínicos
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          data-toggle="modal"
                          data-target="#modalProblemasClinicosForm"
                        >
                          <i className="fas fa-plus mr-1"></i>
                          Adicionar Problema Clínico
                        </button>
                      </div>
                      <div className="border rounded p-3 bg-light">
                        {Object.keys(problemasClinicos).filter(id => problemasClinicos[id]).length === 0 ? (
                          <div className="text-muted text-center py-3">
                            <i className="fas fa-info-circle mr-2"></i>
                            Nenhum problema clínico selecionado. Clique em "Adicionar Problema Clínico" para selecionar.
                          </div>
                        ) : (
                          <div className="row">
                            {Object.keys(problemasClinicos)
                              .filter(id => problemasClinicos[id])
                              .map(problemaId => {
                                const problema = problemasCatalogoLista.find(p => p.id === problemaId);
                                return problema ? (
                                  <div className="col-md-6 mb-2" key={problemaId}>
                                    <div className="d-flex align-items-center justify-content-between bg-white border rounded p-2">
                                      <div className="custom-control custom-checkbox">
                                        <input
                                          type="checkbox"
                                          className="custom-control-input"
                                          id={`problema-selecionado-${problemaId}`}
                                          checked={true}
                                          onChange={() => handleProblemaClinicoChange(problemaId, false)}
                                        />
                                        <label 
                                          className="custom-control-label" 
                                          htmlFor={`problema-selecionado-${problemaId}`}
                                        >
                                          {problema.label}
                                        </label>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleProblemaClinicoChange(problemaId, false)}
                                        title="Remover"
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  </div>
                                ) : null;
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal para selecionar problemas clínicos */}
              <div className="modal fade" id="modalProblemasClinicosForm" tabIndex={-1} role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                  <div className="modal-content">
                    <div className="modal-header bg-success text-white">
                      <h5 className="modal-title">
                        <i className="fas fa-clipboard-list mr-2"></i>
                        Selecionar Problemas Clínicos
                      </h5>
                      <button type="button" className="close text-white" data-dismiss="modal">
                        <span>&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {!catalogosCarregados ? (
                        <div className="text-center p-3">
                          <div className="spinner-border spinner-border-sm mr-2"></div>
                          Carregando problemas clínicos...
                        </div>
                      ) : problemasCatalogoLista.length === 0 ? (
                        <div className="alert alert-warning">
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          Nenhum problema clínico disponível no momento.
                        </div>
                      ) : (
                        <div className="row">
                          {problemasCatalogoLista.map(problema => (
                            <div className="col-md-6 col-sm-6 mb-2" key={problema.id}>
                              <ProblemaClinicoCheckbox
                                problemaId={problema.id}
                                label={problema.label}
                                checked={problemasClinicos[problema.id] || false}
                                onChange={handleProblemaClinicoChange}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" data-dismiss="modal">
                        <i className="fas fa-times mr-1"></i>
                        Fechar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label htmlFor="observacoes" className="form-label">
                      <i className="fas fa-sticky-note mr-1"></i>
                      Observações Gerais
                    </label>
                    {isAtendente ? (
                      <div className="alert alert-warning mb-0">
                        <i className="fas fa-lock mr-1"></i>
                        Campo restrito. Entre em contato com o psicólogo responsável para informações clínicas.
                      </div>
                    ) : (
                      <textarea
                        className="form-control"
                        id="observacoes"
                        rows={3}
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        placeholder="Observações adicionais sobre o paciente..."
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title">
                <i className="fas fa-check mr-2"></i>
                Revisar e Confirmar Dados
              </h3>
            </div>
            <div className="card-body">
              <div className="alert alert-info">
                <h5><i className="fas fa-info-circle mr-1"></i>{' '}Revisão dos Dados</h5>
                <p>Revise todas as informações antes de salvar. Você pode voltar a qualquer passo anterior para fazer alterações.</p>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <h6><i className="fas fa-user mr-1"></i>{' '}Dados Básicos</h6>
                  <ul className="list-unstyled">
                    <li><strong>Nome:</strong> {nome}</li>
                    <li><strong>Telefone:</strong> {telefone}</li>
                    <li><strong>Email:</strong> {email || '(não informado)'}</li>
                    <li><strong>Serviço:</strong> {servico}</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6><i className="fas fa-address-card mr-1"></i>{' '}Informações Complementares</h6>
                  <ul className="list-unstyled">
                    <li><strong>Profissão:</strong> {profissao || '(não informado)'}</li>
                    <li><strong>Endereço:</strong> {endereco || '(não informado)'}</li>
                    <li><strong>Estado Civil:</strong> {estado_civil || '(não informado)'}</li>
                    <li><strong>Escolaridade:</strong> {escolaridade || '(não informado)'}</li>
                  </ul>
                </div>
              </div>

              {(nome_emergencia || telefone_emergencia) && (
                <div className="row mt-3">
                  <div className="col-md-12">
                    <h6><i className="fas fa-phone mr-1"></i>{' '}Contato de Emergência</h6>
                    <ul className="list-unstyled">
                      <li><strong>Nome:</strong> {nome_emergencia || '(não informado)'}</li>
                      <li><strong>Telefone:</strong> {telefone_emergencia || '(não informado)'}</li>
                      <li><strong>Parentesco:</strong> {parentesco_emergencia || '(não informado)'}</li>
                    </ul>
                  </div>
                </div>
              )}

              {(historicoMedico || observacoes) && (
                <div className="row mt-3">
                  <div className="col-md-12">
                    <h6><i className="fas fa-file-medical mr-1"></i>{' '}Informações Médicas</h6>
                    {historicoMedico && (
                      <div className="mb-2">
                        <strong>Histórico Médico:</strong>
                        <div className="bg-light p-2 rounded">{historicoMedico}</div>
                      </div>
                    )}
                    {observacoes && (
                      <div className="mb-2">
                        <strong>Observações:</strong>
                        <div className="bg-light p-2 rounded">{observacoes}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="alert alert-success mt-4">
                <h6><i className="fas fa-check-circle mr-1"></i>{' '}Pronto para Salvar</h6>
                <p className="mb-0">
                  {id ? 
                    'As alterações serão aplicadas ao cadastro do paciente.' : 
                    'O paciente será cadastrado no sistema e estará disponível para agendamento de consultas.'
                  }
                </p>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (carregando) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2 className="mb-2">
            <i className="fas fa-user-plus mr-2"></i>
            {id ? 'Editar Paciente' : 'Cadastrar Novo Paciente'}
          </h2>
          <p className="text-muted">
            {id ? 'Atualize as informações do paciente' : 'Preencha os dados para cadastrar um novo paciente'}
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/pacientes')}
            disabled={salvando}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar à Lista
          </button>
        </div>
      </div>

      {sucessoMensagem && (
        <div className="alert alert-success alert-dismissible">
          <button 
            type="button" 
            className="close" 
            onClick={() => setSucessoMensagem('')}
          >
            <span>&times;</span>
          </button>
          <i className="icon fas fa-check"></i>
          {sucessoMensagem}
        </div>
      )}

      {erro && (
        <div className="alert alert-danger">
          <h6><i className="icon fas fa-exclamation-triangle"></i> Erro:</h6>
          <p>{erro}</p>
        </div>
      )}

      {Object.keys(erros).length > 0 && (
        <div className="alert alert-danger">
          <h6><i className="icon fas fa-exclamation-triangle"></i> Corrija os seguintes erros:</h6>
          <ul className="mb-0">
            {Object.values(erros).map((erro, index) => (
              <li key={index}>{erro}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Indicador de progresso - condicionalmente visível apenas no modo de cadastro */}
      {!modoEdicao && (
        <div className="card">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h6 className="mb-2">Progresso do Cadastro</h6>
                <div className="progress" style={{ height: '10px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    style={{ width: `${progresso()}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  {Math.round(progresso())}% completo
                </small>
              </div>
              <div className="col-md-4 text-right">
                <span className="badge badge-primary badge-lg">
                  Passo {passoAtual} de {totalPassos}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

  {/* Navegação de passos - versão modificada para edição */}
      <div className="card">
        <div className="card-body p-2">
          <div className="row">
            {[1, 2, 3, 4, 5].map(passo => (
              <div key={passo} className="col">
                <div 
                  className={`text-center p-2 rounded cursor-pointer ${
                    passo === passoAtual ? 'bg-primary text-white' : 
                    passo < passoAtual && !modoEdicao ? 'bg-success text-white' : 
                    'bg-light text-muted'
                  }`}
                  onClick={() => irParaPasso(passo)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      irParaPasso(passo);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Ir para passo ${passo}`}
                >
                  <div className="d-flex align-items-center justify-content-center">
                    <i className={`fas ${
                      passo === 1 ? 'fa-user' :
                      passo === 2 ? 'fa-address-card' :
                      passo === 3 ? 'fa-phone' : 
                      passo === 4 ? 'fa-file-medical' : 'fa-check'
                    } mr-2`}></i>
                    <span className="small">
                      {passo === 1 ? 'Básico' :
                       passo === 2 ? 'Detalhes' :
                       passo === 3 ? 'Emergência' : 
                       passo === 4 ? 'Médico' : 'Confirmar'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Conteúdo do passo atual - usando a função auxiliar */}
        {renderizarConteudoPasso(passoAtual)}
        
        {/* Botões de navegação - Desktop/Tablet */}
        <div className="card d-none d-md-block">
          <div className="card-body">
            <div className="row">
              <div className="col-6">
                {(passoAtual > 1 && !modoEdicao) && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={passoAnterior}
                    disabled={salvando}
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Anterior
                  </button>
                )}
              </div>
              <div className="col-6 text-right">
                {/* Lógica diferente para modo de edição vs cadastro */}
                {modoEdicao ? (
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={salvando}
                  >
                    {salvando ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Salvar Alterações
                      </>
                    )}
                  </button>
                ) : (
                  // Modo de cadastro - botões originais
                  <>
                    {passoAtual < totalPassos ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(e) => proximoPasso(e)}  // CORREÇÃO: Passando o objeto de evento
                        disabled={salvando}
                      >
                        Próximo
                        <i className="fas fa-arrow-right ml-2"></i>
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-success btn-lg"
                        disabled={salvando}
                      >
                        {salvando ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2"></span>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save mr-2"></i>
                            Salvar Paciente
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Mobile action bar */}
        <div className="mobile-action-bar d-md-none">
          {(passoAtual > 1 && !modoEdicao) && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={passoAnterior}
              disabled={salvando}
            >
              Anterior
            </button>
          )}
          {modoEdicao ? (
            <button
              type="submit"
              className="btn btn-success"
              disabled={salvando}
            >
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          ) : (
            passoAtual < totalPassos ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => proximoPasso(e)}
                disabled={salvando}
              >
                Próximo
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-success"
                disabled={salvando}
              >
                {salvando ? 'Salvando...' : 'Salvar Paciente'}
              </button>
            )
          )}
        </div>
      </form>
    </div>
  );
}

